import { isBaseUom, normalizeAliasDesc, normalizeUom } from '../uom.js';

export interface SupplierAliasPackFactor {
  food_id: string;
  supplier_code: string | null;
  raw_description_norm: string | null;
  pack_factor: number;
  pack_uom: string | null;
  base_uom: string | null;
}

export interface PackFactorLookup {
  supplierCode: string | null;
  description: string | null;
  extractedUom: string | null;
  foodId: string | null;
  foodUom: string;
}

export interface TrustedPackFactor {
  foodId: string;
  factor: number;
  packUom: string;
  baseUom: string;
  source: 'supplier_code' | 'food' | 'description';
}

function normalizeSupplierCode(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
}

function sameFactor(left: SupplierAliasPackFactor, right: SupplierAliasPackFactor): boolean {
  return (
    left.food_id === right.food_id &&
    left.pack_factor === right.pack_factor &&
    normalizeUom(left.pack_uom) === normalizeUom(right.pack_uom) &&
    normalizeUom(left.base_uom) === normalizeUom(right.base_uom)
  );
}

function validateCandidate(
  row: SupplierAliasPackFactor,
  lookup: PackFactorLookup,
  source: TrustedPackFactor['source'],
  trustExactSupplierItem = false,
): TrustedPackFactor | null {
  if (!Number.isFinite(row.pack_factor) || row.pack_factor <= 0) return null;
  if (!row.pack_uom || (!lookup.extractedUom && !trustExactSupplierItem)) return null;
  const samePackUom = normalizeUom(row.pack_uom) === normalizeUom(lookup.extractedUom);
  if (!samePackUom && !trustExactSupplierItem) return null;
  if (!row.base_uom || !isBaseUom(row.base_uom)) return null;
  if (lookup.foodId && row.food_id !== lookup.foodId) return null;
  if (
    lookup.foodId &&
    (!isBaseUom(lookup.foodUom) || normalizeUom(row.base_uom) !== normalizeUom(lookup.foodUom))
  )
    return null;
  return {
    foodId: row.food_id,
    factor: row.pack_factor,
    packUom: normalizeUom(row.pack_uom),
    baseUom: normalizeUom(row.base_uom),
    source,
  };
}

const DESCRIPTION_NOISE = new Set(['kg', 'gr', 'pz', 'conf', 'ct', 'lt', 'ml', 'vaso', 'busta']);

function descriptionTokens(value: string | null | undefined): Set<string> {
  return new Set(
    normalizeAliasDesc(value ?? '')
      .split(/[^a-z0-9à-ù]+/i)
      .map((token) => token.trim())
      .filter((token) => token.length >= 3 && !DESCRIPTION_NOISE.has(token)),
  );
}

function descriptionsAreStronglyCompatible(stored: string | null, current: string): boolean {
  const left = descriptionTokens(stored);
  const right = descriptionTokens(current);
  if (left.size < 2 || right.size < 2) return false;
  const shared = Array.from(left).filter((token) => right.has(token)).length;
  if (shared < 2) return false;
  const smallerCoverage = shared / Math.min(left.size, right.size);
  const union = new Set([...left, ...right]).size;
  return smallerCoverage >= 0.75 && shared / union >= 0.5;
}

/**
 * Recupera un fattore solo da memoria non ambigua.
 *
 * Il codice articolo del fornitore è la chiave primaria. La descrizione resta
 * un fallback solo se trova una memoria unica e fortemente compatibile: serve
 * a recuperare OCR imperfetti come 1663B al posto di 16657. Qualsiasi
 * ambiguità tra alimento, fattore o unità chiude il lookup senza conversione.
 */
export function findTrustedPackFactor(
  rows: SupplierAliasPackFactor[],
  lookup: PackFactorLookup,
): TrustedPackFactor | null {
  const code = normalizeSupplierCode(lookup.supplierCode);
  if (code) {
    const byCode = rows.filter((row) => normalizeSupplierCode(row.supplier_code) === code);
    if (byCode.length > 0) {
      const first = byCode[0];
      if (!byCode.every((row) => sameFactor(first, row))) return null;
      return validateCandidate(first, lookup, 'supplier_code', true);
    }
  }

  const description = normalizeAliasDesc(lookup.description ?? '');
  const exact = description
    ? rows.filter((row) => row.raw_description_norm === description)
    : [];
  if (exact.length > 0) {
    const first = exact[0];
    if (exact.every((row) => sameFactor(first, row))) {
      // L'UdM estratta descrive la quantità del documento e può oscillare tra
      // pz/cassa/ct. Per lo stesso articolo fornitore conta l'UdM reale di
      // destinazione, già verificata sotto contro l'anagrafica di magazzino.
      return validateCandidate(first, lookup, 'description', true);
    }
  }

  // Una correzione può collegare una nuova dicitura del DDT a un alimento già
  // noto prima che l'alias testuale sia stato scritto. Se quello stesso
  // articolo-fornitore ha UN SOLO fattore compatibile, riusiamolo: al commit
  // la nuova dicitura verrà consolidata come alias distinto. Più fattori per
  // lo stesso alimento restano ambigui (formati/confezioni diversi) e non
  // vengono mai scelti automaticamente.
  if (lookup.foodId) {
    const byFood = rows.filter((row) => row.food_id === lookup.foodId);
    if (byFood.length > 0) {
      const first = byFood[0];
      if (byFood.every((row) => sameFactor(first, row))) {
        return validateCandidate(first, lookup, 'food');
      }
    }
  }

  if (!description) return null;
  const byDescription = rows.filter((row) =>
    descriptionsAreStronglyCompatible(row.raw_description_norm, description),
  );
  if (byDescription.length === 0) return null;
  const first = byDescription[0];
  if (!byDescription.every((row) => sameFactor(first, row))) return null;
  return validateCandidate(first, lookup, 'description');
}
